<script setup lang="ts">
import {
  ref,
  onMounted,
  onActivated,
  onDeactivated,
  nextTick,
  onUnmounted,
  computed,
  getCurrentInstance,
  watch,
  type PropType,
} from 'vue';
import type { Column, GridOption, Grouping } from 'slickgrid';
import { isArray, isString, debounce } from 'lodash';

import { useSlickGrid } from './useSlickGrid';
import * as messages from './translations';
import VueSlickgridColumnSelector from './VueSlickgridColumnSelector.vue';
import './styles.scss';

const props = defineProps({
  name: { type: String, required: true },
  items: {
    type: [Array, Function] as PropType<any[] | (() => Promise<any[]>)>,
    default: () => [],
  },
  backendUrl: { type: String, default: null },
  columns: { type: Array as PropType<Column<any>[]>, required: true },
  columnDefaults: {
    type: Object as PropType<Partial<Column<any>>>,
    default: () => ({}),
  },
  options: { type: Object as PropType<GridOption>, default: () => ({}) },
  stateKey: { type: String, default: null },
  grouping: {
    type: [Array, Object] as PropType<Grouping<any> | Grouping<any>[]>,
    default: null,
  },
  resizeTrigger: { type: [Number, String, Boolean], default: 0 },
  noColumnReorder: { type: Boolean, default: false },
  editCommandHandler: { type: Function, default: null },

  noHeader: { type: Boolean, default: false },
  headerTitle: { type: String, default: '' },
  headerShowRowCount: { type: Boolean, default: true },
  headerShowReloadButton: { type: Boolean, default: true },
  headerShowExportButton: { type: Boolean, default: true },
  headerShowColumnSelectorButton: { type: Boolean, default: true },
  footerTotals: { type: Boolean, default: false },

  checkboxRowSelection: { type: Boolean, default: false },
  rowHeader: { type: Boolean, default: false },

  selectionModel: { type: Object, default: null },
  rowClass: { type: [Function, String], default: null },

  headerRowFilter: { type: Boolean, default: true },
  noTooltips: { type: Boolean, default: false },
  tooltipsEnableForCells: { type: Boolean, default: true },
  tooltipsEnableForHeaderCells: { type: Boolean, default: true },
  tooltipsMaxToolTipLength: { type: Number, default: null },

  exportToCsvFilename: { type: String, default: 'export' },
  exportToCsvQuotechar: { type: String, default: '"' },
  exportToCsvDelimiter: { type: String, default: ';' },
});

const emit = defineEmits([
  'dataLoaded',
  'error',
  'rowsChanged',
  'selectedRowsChanged',
  'scroll',
  'sort',
  'click',
  'dblClick',
  'contextmenu',
  'reload',
  'command',
]);

const gridContainerElement = ref<HTMLDivElement | null>(null);
const isExporting = ref(false);

const {
  slickGridInstance,
  localDataInstance,
  initGrid,
  isLoading,
  totalRowCount,
  visibleRowCount,
  isFilteredView,
  selectedIds,
  allSelectedIds,
  getSelectedItems,
  getAllSelectedItems,
  clearSelection,
  updateSelectedRows: _updateSelectedRows,
  exportData: _exportData,
} = useSlickGrid({ props, emit });

const currentMessages = computed(() => {
  const locale = (window as any).$app?.i18n?.locale || 'hu';
  return (messages as any)[locale] || messages.hu;
});

const selectedRowCount = computed(() => {
  return selectedIds.value ? selectedIds.value.length : 0;
});

const recordStatusText = computed(() => {
  if (visibleRowCount.value !== totalRowCount.value) {
    return `${visibleRowCount.value} / ${totalRowCount.value}`;
  }
  return `${totalRowCount.value}`;
});

const recordStatusTitle = computed(() => {
  if (visibleRowCount.value !== totalRowCount.value) {
    const filteredText =
      currentMessages.value.records_filtered_text || 'filtered';
    const totalText = currentMessages.value.records_total_text || 'total';
    return `${visibleRowCount.value} ${filteredText} ${totalRowCount.value} ${totalText}`;
  }
  return '';
});

const reloadGrid = async () => {
  if (localDataInstance.value) {
    await localDataInstance.value.reload();
  }
  emit('reload');
};

const exportData = async () => {
  if (!slickGridInstance.value) {
    return;
  }

  isExporting.value = true;

  try {
    await _exportData('csv');
  } catch (error) {
    console.error('Export failed', error);
  } finally {
    setTimeout(() => (isExporting.value = false), 1000);
  }
};

const onResize = debounce(() => {
  slickGridInstance.value?.resizeCanvas();
}, 250);

watch(
  () => props.resizeTrigger,
  (oldVal, newVal) => {
    if (oldVal !== newVal) {
      nextTick(() => slickGridInstance.value?.resizeCanvas());
    }
  },
);

const instance = getCurrentInstance();
const $app = (instance?.proxy as any)?.$root?.$app;

const onGlobalReload = (args: { grid: string | string[] }) => {
  if (isArray(args.grid) && args.grid.indexOf(props.name) > -1) {
    return reloadGrid();
  }
  if (
    isString(args.grid) &&
    (args.grid === 'all' || args.grid === props.name)
  ) {
    return reloadGrid();
  }
};

const onGlobalResize = () => {
  nextTick(() => slickGridInstance.value?.resizeCanvas());
};

onActivated(() => {
  nextTick(() => {
    slickGridInstance.value?.resizeCanvas();
  });
});

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  nextTick(() => {
    if (gridContainerElement.value) {
      initGrid(gridContainerElement.value);
      onResize();

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => onResize());
        resizeObserver.observe(gridContainerElement.value);
      }
    }
  });
  window.addEventListener('resize', onResize);

  if ($app?.events) {
    $app.events.on('slickgrid:reload', onGlobalReload);
    $app.events.on('slickgrid:resize', onGlobalResize);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);

  resizeObserver?.disconnect();
  resizeObserver = null;

  if ($app?.events) {
    $app.events.off('slickgrid:reload', onGlobalReload);
    $app.events.off('slickgrid:resize', onGlobalResize);
  }
});

defineExpose({
  grid: slickGridInstance,
  gridData: localDataInstance,
  reload: reloadGrid,
  getSelectedItems,
  getAllSelectedItems,
  clearSelection,
  selectedIds,
  allSelectedIds,
  updateSelectedRows: _updateSelectedRows,
});
</script>

<template>
  <div class="slickg" :class="name">
    <div v-if="!noHeader" class="d-flex flex-row align-items-center pb-3">
      <slot name="header">
        <strong v-if="headerTitle" class="mx-2 text-muted text-nowrap">
          {{ headerTitle }}
        </strong>
        <span v-if="headerTitle" class="mx-2"> · </span>

        <div
          v-if="headerShowRowCount && totalRowCount > 0"
          class="slickg__rowcount border mx-2 text-nowrap px-2"
          :title="recordStatusTitle"
          :class="{ 'bg-warning': isFilteredView }"
        >
          {{ recordStatusText }}
        </div>

        <div class="btn-group mx-3" role="group">
          <button
            v-if="headerShowReloadButton"
            type="button"
            :disabled="isLoading"
            class="btn btn-light btn-sm border"
            :title="currentMessages.button_reload_title || 'Reload'"
            @click="reloadGrid"
          >
            <i
              class="fa fa-refresh"
              :class="{ 'fa-spin': isLoading }"
              aria-hidden="true"
            />
            <span class="visually-hidden">Reload</span>
          </button>

          <button
            v-if="headerShowExportButton"
            type="button"
            :disabled="isExporting"
            class="btn btn-light btn-sm border"
            :title="currentMessages.button_export_title || 'Export'"
            @click="exportData"
          >
            <i class="fa fa-download" aria-hidden="true" />
            <span class="visually-hidden">Export</span>
          </button>

          <VueSlickgridColumnSelector
            v-if="
              slickGridInstance &&
              headerShowColumnSelectorButton &&
              currentMessages
            "
            :grid="slickGridInstance"
            :columns="columns"
            :messages="currentMessages"
          />
        </div>

        <slot
          name="toolbar"
          :selected-row-count="selectedRowCount"
          :selected-ids="selectedIds"
        />
      </slot>
    </div>

    <div class="slickg-container position-relative">
      <div ref="gridContainerElement" class="slickgrid" />

      <slot name="loading-indicator" :loading="isLoading">
        <b-overlay
          :show="isLoading"
          variant="light"
          spinner-variant="primary"
          opacity=".3"
          class="slickg-container"
          blur=""
          no-wrap
        >
          <strong>{{ currentMessages.overlay_loading_text }}</strong>
        </b-overlay>
      </slot>
    </div>
  </div>
</template>
